import { useEffect, useRef } from "react";
import type { RentalType } from "@/components/checkout/CheckoutRentalTypeEditor";

type PaymentTiming = "pay_now" | "pay_later";
type CancellationPolicy = "non-refundable" | "flexible";

export interface CheckoutDraftValues {
  messageToOwner: string;
  workTrip: boolean;
  businessName: string;
  vatNumber: string;
  selectedCancellationPolicy: CancellationPolicy;
  appliedPromoCode: string;
  climateOffsetEnabled: boolean;
  paymentTiming: PaymentTiming;
  selectedPaymentMethod: string;
  selectedInsuranceId: number | null;
  occasion?: string;
}

export interface CheckoutDraftSetters {
  setMessageToOwner: (v: string) => void;
  setWorkTrip: (v: boolean) => void;
  setBusinessName: (v: string) => void;
  setVatNumber: (v: string) => void;
  setSelectedCancellationPolicy: (v: CancellationPolicy) => void;
  setAppliedPromoCode: (v: string) => void;
  setClimateOffsetEnabled: (v: boolean) => void;
  setPaymentTiming: (v: PaymentTiming) => void;
  setSelectedPaymentMethod: (v: string) => void;
  setPaymentMethodTouched: (v: boolean) => void;
  setSelectedInsuranceId: (v: number | null) => void;
  setOccasion: (v: string | undefined) => void;
}

/**
 * Session backup of in-progress fields in the caller's owner/flow-scoped key.
 * Survives an accidental browser-back + forward, separate from the inline
 * editors which never unmount the page. Cleared on successful booking by the
 * caller (which owns `draftStorageKey`). Restore happens once per mount, gated
 * on `loading` being false so we don't blow away first-paint defaults.
 *
 * NOTE: extracted verbatim from CheckoutPage so hook ordering stays stable
 * across the page's conditional early returns — Rules of Hooks. The restore
 * effect keeps its original `[draftStorageKey, loading]` dep list (and the
 * accompanying exhaustive-deps suppression) so it still fires exactly once
 * when loading flips false.
 */
export function useCheckoutDraft(params: {
  draftStorageKey: string | null;
  loading: boolean;
  rentalType: RentalType;
  /** Task #8092 — when the checkout carries a host "Build a trip" quote
   *  token, the cancellation-policy default is owned by the custom-quote
   *  auto-flip (non-refundable = the host's quoted price). A stale draft
   *  from an earlier ordinary visit to the same car must NOT restore
   *  "flexible" over it — the restore can land AFTER the auto-flip (draft
   *  restore waits for `loading` to clear; the trip quote can resolve
   *  first) and would silently re-add the +10% refundable surcharge to an
   *  Apple Pay charge. An explicit in-session switch to flexible is
   *  unaffected — that goes through the selection handler, not the draft. */
  skipCancellationPolicyRestore?: boolean;
  values: CheckoutDraftValues;
  setters: CheckoutDraftSetters;
}): void {
  const { draftStorageKey, loading, rentalType, skipCancellationPolicyRestore, values, setters } = params;
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    if (!draftStorageKey || draftRestoredRef.current) return;
    if (loading) return;
    try {
      const raw = sessionStorage.getItem(draftStorageKey);
      if (!raw) { draftRestoredRef.current = true; return; }
      const draft = JSON.parse(raw) as Record<string, unknown>;
      if (draftStorageKey.includes(":anonymous:") &&
          (typeof draft.expiresAt !== "number" || Date.now() >= draft.expiresAt)) {
        sessionStorage.removeItem(draftStorageKey);
        draftRestoredRef.current = true;
        return;
      }
      // Discard pre-retirement split drafts instead of silently restoring
      // their saved-card selection into the full-payment checkout.
      if (draft.paymentPlan === "split") {
        sessionStorage.removeItem(draftStorageKey);
        return;
      }
      if (typeof draft.messageToOwner === "string" && !values.messageToOwner) setters.setMessageToOwner(draft.messageToOwner);
      if (typeof draft.workTrip === "boolean") setters.setWorkTrip(draft.workTrip);
      if (typeof draft.businessName === "string") setters.setBusinessName(draft.businessName);
      if (typeof draft.vatNumber === "string") setters.setVatNumber(draft.vatNumber);
      if (
        !skipCancellationPolicyRestore &&
        (draft.selectedCancellationPolicy === "non-refundable" || draft.selectedCancellationPolicy === "flexible")
      ) {
        setters.setSelectedCancellationPolicy(draft.selectedCancellationPolicy);
      }
      if (typeof draft.appliedPromoCode === "string" && draft.appliedPromoCode) setters.setAppliedPromoCode(draft.appliedPromoCode);
      if (typeof draft.climateOffsetEnabled === "boolean") setters.setClimateOffsetEnabled(draft.climateOffsetEnabled);
      if (draft.paymentTiming === "pay_now" || draft.paymentTiming === "pay_later") setters.setPaymentTiming(draft.paymentTiming);
      if (typeof draft.selectedPaymentMethod === "string") {
        setters.setSelectedPaymentMethod(draft.selectedPaymentMethod);
        // The renter explicitly picked this method on the previous visit —
        // count it as touched so the saved-draft hint surfaces post-restore.
        setters.setPaymentMethodTouched(true);
      }
      // Coverage is restored only from the owner-bound opaque flow. A draft
      // preference must never overwrite an explicit choice or grant confirmation.
      if (rentalType !== "driving" && typeof draft.occasion === "string") {
        setters.setOccasion(draft.occasion);
      }
    } catch {
      // ignore corrupt draft
    } finally {
      draftRestoredRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftStorageKey, loading]);

  const {
    messageToOwner, workTrip, businessName, vatNumber, selectedCancellationPolicy,
    appliedPromoCode, climateOffsetEnabled, paymentTiming,
    selectedPaymentMethod, selectedInsuranceId, occasion,
  } = values;

  useEffect(() => {
    if (!draftStorageKey || !draftRestoredRef.current) return;
    try {
      sessionStorage.setItem(draftStorageKey, JSON.stringify({
        ...(draftStorageKey.includes(":anonymous:") ? { expiresAt: Date.now() + 30 * 60 * 1000 } : {}),
        messageToOwner, workTrip, businessName, vatNumber, selectedCancellationPolicy,
        appliedPromoCode, climateOffsetEnabled, paymentTiming,
        selectedPaymentMethod, selectedInsuranceId, occasion,
      }));
    } catch {
      // sessionStorage may be unavailable (private browsing) — ignore.
    }
  }, [draftStorageKey, messageToOwner, workTrip, businessName, vatNumber, selectedCancellationPolicy, appliedPromoCode, climateOffsetEnabled, paymentTiming, selectedPaymentMethod, selectedInsuranceId, occasion]);
}
